import { Immutable } from "immer";
import React from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { StoreCategory } from "tsi-common-react/src/api/retail";
import {
    MarkerMap,
    type MarkerMapHandle,
} from "tsi-common-react/src/apps/retail/MarkerMap/index";
import { LoadingSpinner } from "tsi-common-react/src/components/LoadingSpinner/index";
import { RetailStoreEventType } from "tsi-common-react/src/constants";
import { RetailStore } from "tsi-common-react/src/models/location";
import { SyncStoreID } from "tsi-common-react/src/models/nominals";
import { trackRetailStoreEvent } from "tsi-common-react/src/utils/analytics";
import { usePageSetting } from "tsi-common-react/src/utils/settings";
import { t } from "ttag";

import { constants } from "@reactivated/constants";

import { LocatorNearestStoresRow } from "../LocatorNearestStoresRow";
import { LocatorSendResultsEmail } from "../LocatorSendResults_Email";
import { LocatorSendResultsPrint } from "../LocatorSendResults_Print";
import { LocatorSendResultsText } from "../LocatorSendResults_Text";
import { NoResults } from "../NoResults";
import { groupStores } from "../utils";

const AppSlug = constants["tsicommon.types.AppSlug"];

const getStoreNumber = (
    stores: Immutable<RetailStore[]>,
    store: Immutable<RetailStore>,
): number => {
    return stores.indexOf(store) + 1;
};

const InfoWindowContent = ({
    store,
    linkStoreDetails,
}: {
    store: Immutable<RetailStore>;
    linkStoreDetails: boolean;
}): React.ReactElement => {
    const url = `/locations/states/${store.state}/cities/${store.city}/stores/${store.id}/`;
    return (
        <div className="loc__info-window">
            <div className="loc-name">
                {linkStoreDetails ? (
                    <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={store.name}
                    >
                        {store.name}
                    </a>
                ) : (
                    <span title={store.name}>{store.name}</span>
                )}
            </div>
            <div className="loc-content">
                <div className="loc-address">{store.address}</div>
            </div>
        </div>
    );
};

const createInfoWindowContent = (
    store: Immutable<RetailStore>,
    linkStoreDetails: boolean,
): string => {
    return renderToStaticMarkup(
        <InfoWindowContent store={store} linkStoreDetails={linkStoreDetails} />,
    );
};

const trackStoreAnalytics = (
    storeGroup: {
        main: Immutable<RetailStore>;
        secondary: Immutable<RetailStore[]>;
    },
    storeID: SyncStoreID,
): void => {
    const store =
        storeGroup.main.external_id === storeID
            ? storeGroup.main
            : storeGroup.secondary.find((s) => s.external_id === storeID);
    if (store) {
        trackRetailStoreEvent(
            store.data?.classification_tempur === StoreCategory.FLAGSHIP
                ? RetailStoreEventType.TEMPUR_STORE
                : RetailStoreEventType.RETAIL_STORE,
            store,
        );
    }
};

const createMarkerOptions = (
    stores: Immutable<RetailStore[]>,
    store: Immutable<RetailStore>,
): google.maps.MarkerOptions => {
    return {
        icon: {
            path: `M12.964,0.542c-6.895,0 -12.485,5.463 -12.485,12.201c0,3.118 5.324,12.063 7.134,15.251c2.198,3.869
            5.351,10.894 5.351,10.894c0,0 3.153,-7.025 5.351,-10.894c1.81,-3.188 7.134,-12.133 7.134,-15.251c0,-6.738
            -5.59,-12.201 -12.485,-12.201Z`,
            fillColor: "#3C4652",
            fillOpacity: 1,
            anchor: new google.maps.Point(10, 29),
            strokeWeight: 2,
            strokeColor: "#ffffff",
            scale: 1,
            labelOrigin: new google.maps.Point(14, 14),
        },
        label: {
            text: `${getStoreNumber(stores, store)}`,
            color: "#fff",
            fontSize: "12px",
            fontWeight: "light",
        },
    };
};

const createMapOptions = (): google.maps.MapOptions => {
    return {
        scrollwheel: false,
        draggable: true,
        disableDefaultUI: false,
        clickableIcons: false,
        mapTypeId: "roadmap",
    };
};

export const LocatorNearestStores = (props: {
    stores: Immutable<RetailStore[]>;
    focusedStoreID: SyncStoreID | null;
    onSelectStore: (storeID: SyncStoreID | null) => void;
    showMore: boolean;
    disableGrouping?: boolean;
    noResultsText?: string;
    isLoadingStores?: boolean;
}): React.ReactNode => {
    const mapElem = React.useRef<MarkerMapHandle>(null);
    const appSlug = usePageSetting("app_slug") || "";
    const linkStoreDetailsInInfoWindow =
        usePageSetting("retail_locator_show_store_info_links") ?? true;

    const handleBeforeInfoWindowOpen = (
        info: google.maps.InfoWindow,
        store: Immutable<RetailStore>,
    ) => {
        const content = createInfoWindowContent(
            store,
            linkStoreDetailsInInfoWindow,
        );
        info.setContent(content);
        props.onSelectStore(store.external_id);
    };

    const handleInfoWindowClose = () => {
        props.onSelectStore(null);
    };

    const getStoreNumberForStore = (store: Immutable<RetailStore>) => {
        return getStoreNumber(props.stores, store);
    };

    const groups = props.disableGrouping
        ? props.stores.map((store) => {
              return {
                  main: store,
                  secondary: [],
              };
          })
        : groupStores(props.stores);

    const renderRows = () => {
        // Show loading state while stores are being fetched
        if (props.isLoadingStores) {
            return <LoadingSpinner />;
        }
        if (groups.length <= 0) {
            return (
                <NoResults retailerName={props.noResultsText || t`stores`} />
            );
        }
        return groups.map((storeGroup) => {
            return (
                <LocatorNearestStoresRow
                    key={`${storeGroup.main.external_id}`}
                    store={storeGroup.main}
                    nestedStores={storeGroup.secondary}
                    selectedStoreID={props.focusedStoreID}
                    getStoreNumber={getStoreNumberForStore}
                    onSelectStore={(storeID, event) => {
                        event.stopPropagation();
                        mapElem.current?.onActivateMarker(storeID);
                        trackStoreAnalytics(storeGroup, storeID);
                    }}
                />
            );
        });
    };
    const isCanada = appSlug === AppSlug.TEMPURCA;
    return (
        <>
            <div className="locations__map-wrapper">
                <MarkerMap
                    ref={mapElem}
                    stores={props.stores}
                    markerOptions={(store) => {
                        return createMarkerOptions(props.stores, store);
                    }}
                    elemProps={{
                        "id": "locations__map",
                        "className": "bh-sl-map",
                        "role": "region",
                        "aria-label": t`map`,
                    }}
                    mapOptions={createMapOptions}
                    onBeforeInfoWindowOpen={handleBeforeInfoWindowOpen}
                    onInfoWindowClose={handleInfoWindowClose}
                />
                {!isCanada && (
                    <div className="locations__extras">
                        <div className="send">
                            <LocatorSendResultsEmail
                                stores={props.stores}
                                getStoreNumber={getStoreNumberForStore}
                            />
                            <LocatorSendResultsPrint
                                stores={props.stores}
                                getStoreNumber={getStoreNumberForStore}
                            />
                            <LocatorSendResultsText
                                stores={props.stores}
                                getStoreNumber={getStoreNumberForStore}
                            />
                        </div>
                    </div>
                )}
            </div>
            <section className="locations__list-wrapper">
                <ul className="locations__list list">{renderRows()}</ul>
                {props.showMore && (
                    <div>
                        <a href="#all">{t`See More Results`}</a>
                    </div>
                )}
            </section>
        </>
    );
};
